import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import ReasonDialog from "../common/ReasonDialog";
import { usePagedList } from "../common/usePagedList";
import { bulkRejectRegistrations, listReviewQueue } from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate, formatDateTime } from "../common/format";
import { dateSourceLabel, reviewReasonLabel } from "../common/opsLabels";
import { messageForCode } from "../common/errorMessages";
import { isAmbiguousFailure, requestIdFor, settleRequestId } from "../common/clientRequest";
import {
  BULK_REJECT_MAX,
  DEFAULT_REVIEW_GROUP,
  REVIEW_GROUPS,
  REVIEW_PAGE_SIZE,
  bulkSummary,
  failedRows,
  groupCount,
  isSelectable,
  keepShown,
  registrationIdsOf,
  selectableIds,
} from "../common/reviewQueueList";
import ReviewDecision from "./ReviewDecision";
import "../warranty.css";

const GROUP_HINT = {
  live: "Registrations from the app and the dealers waiting for a person to decide.",
  legacy:
    "Rows the import of the old warranty records flagged. Most are on active registrations: " +
    "rejecting one of those is skipped, nothing changes.",
  all: "Every open row, new and old.",
};

const ReviewQueue = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.REVIEW);
  useWarrantyNav(allowed);

  // A link from the warranty journey names one open row (?item=<queue row id>): the queue opens on
  // All and that row's decision panel opens once the first page shows it.
  const linkedItem = useRef(new URLSearchParams(window.location.search).get("item"));
  const [group, setGroup] = useState(linkedItem.current ? "all" : DEFAULT_REVIEW_GROUP);
  const fetchPage = useCallback(
    (cursor) => listReviewQueue(accessToken, cursor, group, REVIEW_PAGE_SIZE),
    [accessToken, group]
  );
  const list = usePagedList(fetchPage, allowed);
  const [selectedItem, setSelectedItem] = useState(null);

  // The counts of the last answer stay on the tabs while another group loads.
  const [lastCounts, setLastCounts] = useState(null);
  const firstCounts = list.firstPage?.counts;
  useEffect(() => {
    if (firstCounts) {
      setLastCounts(firstCounts);
    }
  }, [firstCounts]);
  const counts = firstCounts || lastCounts;

  // Selected rows by queue row id; only rows still shown stay selected.
  const [selected, setSelected] = useState(() => new Set());
  const { items } = list;
  useEffect(() => {
    setSelected((prev) => keepShown(prev, items));
  }, [items]);

  useEffect(() => {
    if (!linkedItem.current || list.status !== "ready") {
      return;
    }
    const row = items.find((item) => item.id === linkedItem.current);
    linkedItem.current = null;
    if (row) {
      setSelectedItem(row);
    }
  }, [list.status, items]);

  const [confirming, setConfirming] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const bulkRequestRef = useRef(null);

  const choose = (key) => {
    if (key === group || bulkRunning) {
      return;
    }
    setSelected(new Set());
    setOutcome(null);
    setGroup(key);
  };

  const selectable = items.filter(isSelectable);
  const pageIds = selectableIds(items);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const registrationIds = registrationIdsOf(selected, items);
  const tooMany = registrationIds.length > BULK_REJECT_MAX;

  const toggleAll = () => {
    setSelected(allOnPage ? new Set() : new Set(pageIds));
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const rejectSelected = async (reason) => {
    const ids = registrationIds;
    const fingerprint = JSON.stringify({ ids, reason });
    const requestId = requestIdFor(bulkRequestRef, fingerprint);
    setBulkRunning(true);
    const result = await bulkRejectRegistrations(accessToken, ids, reason, requestId);
    setBulkRunning(false);
    settleRequestId(bulkRequestRef, result);
    if (!result.ok) {
      return { ok: false, message: result.message, unsure: isAmbiguousFailure(result) };
    }
    const failed = failedRows(result.data, items);
    setOutcome({ summary: bulkSummary(result.data), failed });
    toast.success(bulkSummary(result.data));
    setSelected(new Set());
    list.reload(false);
    return { ok: true };
  };

  const decided = (item) => {
    setSelectedItem(null);
    list.removeWhere((row) => row.id === item.id);
    toast.success("The decision was recorded.");
  };

  const stale = () => {
    setSelectedItem(null);
    list.reload(true);
  };

  const countText = (key) => {
    const value = groupCount(counts, key);
    return value === null ? "" : value.toLocaleString("en-IN");
  };

  return (
    <WarrantyPage
      title="Review queue"
      sub="Registrations waiting for a person to decide. Market IN."
      allowed={allowed}
      deniedTitle="You do not have access to the review queue"
      deniedText="Ask an admin to give you the Warranty Review Queue permission."
      actions={
        <>
          <Button
            className="Addbtn"
            onClick={() => setConfirming(true)}
            disabled={registrationIds.length === 0 || tooMany || bulkRunning}
          >
            {bulkRunning ? "Rejecting" : `Reject selected (${registrationIds.length})`}
          </Button>{" "}
          <Button
            className="Addbtn"
            onClick={() => list.reload(false)}
            disabled={list.status === "loading" || bulkRunning}
          >
            Reload
          </Button>
        </>
      }
    >
      <div className="warranty-tabs" role="tablist" aria-label="Review queue groups">
        {REVIEW_GROUPS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`warranty-review-tab-${key}`}
            aria-selected={group === key}
            aria-controls="warranty-review-panel"
            className={`warranty-tab${group === key ? " is-active" : ""}`}
            onClick={() => choose(key)}
            disabled={bulkRunning}
          >
            {label} <span className="warranty-tab-count">{countText(key)}</span>
          </button>
        ))}
      </div>
      <div
        id="warranty-review-panel"
        role="tabpanel"
        aria-labelledby={`warranty-review-tab-${group}`}
      >
        <p className="warranty-dialog-hint">{GROUP_HINT[group]}</p>
        {outcome && (
          <div className="warranty-banner is-warning" role="status">
            <span>
              {outcome.summary}
              {outcome.failed.length > 0 && (
                <>
                  {" "}
                  Not rejected:{" "}
                  {outcome.failed
                    .slice(0, 10)
                    .map((row) => `${row.frameNumber} (${messageForCode(row.code)})`)
                    .join("; ")}
                  {outcome.failed.length > 10 ? ` and ${outcome.failed.length - 10} more.` : ""}
                </>
              )}
            </span>
            <button
              type="button"
              className="warranty-inline-btn"
              onClick={() => setOutcome(null)}
            >
              Dismiss
            </button>
          </div>
        )}
        {tooMany && (
          <p className="warranty-field-msg is-warning" role="status">
            Select at most {BULK_REJECT_MAX} registrations at a time.
          </p>
        )}
        {list.status === "loading" && <Loading label="Loading the review queue" />}
        {list.status === "error" && (
          <ErrorState message={list.message} onRetry={() => list.reload(false)} />
        )}
        {list.status === "ready" && items.length === 0 && (
          <EmptyState title="Nothing in this list">
            <span>Nothing here is waiting for a decision.</span>
          </EmptyState>
        )}
        {list.status === "ready" && items.length > 0 && (
          <>
            <div className="tableView warranty-ops-table">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allOnPage}
                        onChange={toggleAll}
                        disabled={selectable.length === 0 || bulkRunning}
                        aria-label={`Select all on this page, up to ${BULK_REJECT_MAX}`}
                        title={`Select all on this page, up to ${BULK_REJECT_MAX}`}
                      />
                    </th>
                    <th scope="col">Reason</th>
                    <th scope="col">Frame number</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Created</th>
                    <th scope="col">Purchase date</th>
                    <th scope="col">
                      <span className="warranty-sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selected.has(item.id)}
                          onChange={() => toggle(item.id)}
                          disabled={!isSelectable(item) || bulkRunning}
                          aria-label={`Select ${item.frameNumber}`}
                          title={
                            isSelectable(item)
                              ? undefined
                              : "An ownership claim is decided on its own"
                          }
                        />
                      </td>
                      <td>{reviewReasonLabel(item.reason)}</td>
                      <td>{item.frameNumber}</td>
                      <td>{item.maskedPhone || "-"}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td>
                        {formatDate(item.purchaseDate)}
                        {item.purchaseDateSource && (
                          <div className="warranty-muted">
                            {dateSourceLabel(item.purchaseDateSource)}
                          </div>
                        )}
                      </td>
                      <td className="warranty-actions-cell">
                        <button
                          type="button"
                          className="warranty-inline-btn is-primary"
                          onClick={() => setSelectedItem(item)}
                          aria-label={`Review ${item.frameNumber}`}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="warranty-more">
              {list.moreError && (
                <span className="warranty-field-msg is-error" role="alert">
                  {list.moreError}
                </span>
              )}
              {list.nextCursor && (
                <Button className="Addbtn" onClick={list.loadMore} disabled={list.loadingMore}>
                  {list.loadingMore ? "Loading" : "Load more"}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <ReasonDialog
        show={confirming}
        title={`Reject ${registrationIds.length} ${
          registrationIds.length === 1 ? "registration" : "registrations"
        }?`}
        explanation={
          `Each selected registration is rejected as if you rejected it on its own, and the rider ` +
          `sees it as rejected. A row that is no longer waiting for a decision is skipped. The ` +
          `reason is kept in the audit log.`
        }
        confirmLabel={`Reject ${registrationIds.length}`}
        onConfirm={rejectSelected}
        onClose={() => setConfirming(false)}
      />

      {selectedItem && (
        <ReviewDecision
          key={selectedItem.id}
          item={selectedItem}
          accessToken={accessToken}
          onClose={() => setSelectedItem(null)}
          onDecided={decided}
          onStale={stale}
        />
      )}
    </WarrantyPage>
  );
};

export default ReviewQueue;
